import { z } from "zod";
import { IsoUtc, PropertyId, Uuid } from "./primitives";

export const AnalyticsEventType = z.enum([
  "town_session_start",
  "room_entry",
  "panel_view",
  "sign_impression",
  "outbound_click",
  "outbound_redirect_raw",
]);
export type AnalyticsEventType = z.infer<typeof AnalyticsEventType>;

/** Client-originated measurement candidates. Validated, rate-limited, and deduped server-side. */
export const ClientAnalyticsEvent = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("panel_view"),
      propertyId: PropertyId,
      clientEventId: z.string().min(8).max(64),
    })
    .strict(),
  z
    .object({
      type: z.literal("sign_impression"),
      propertyId: PropertyId,
      clientEventId: z.string().min(8).max(64),
      /** Fraction of projected sign area inside the viewport at the end of the window (0..1). */
      visibleFraction: z.number().min(0).max(1),
      /** Continuous visible milliseconds measured by the client (bounded; server re-checks plausibility). */
      continuousMs: z.number().int().min(0).max(600_000),
      signKind: z.enum(["storefront", "skyline"]),
    })
    .strict(),
]);
export type ClientAnalyticsEvent = z.infer<typeof ClientAnalyticsEvent>;

export const ClientAnalyticsBatch = z.object({ events: z.array(ClientAnalyticsEvent).min(1).max(25) }).strict();
export type ClientAnalyticsBatch = z.infer<typeof ClientAnalyticsBatch>;

export const ANALYTICS_DEFINITIONS_VERSION = 1;

export const StatsRange = z.enum(["today", "yesterday", "last7d", "last30d", "lifetime"]);
export type StatsRange = z.infer<typeof StatsRange>;

const Metric = z.number().int().min(0).nullable();
export const OverviewMetrics = z.object({
  townSessions: Metric,
  uniqueVisitors: Metric,
  signImpressions: Metric,
  panelViews: Metric,
  roomEntries: Metric,
  outboundClicksOrdinary: Metric,
  outboundClicksRewarded: Metric,
  /**
   * ADR-0031: coins residents earned from offers (the SPONSORED_REWARD coin journals; test and staff excluded). A
   * virtual reward with no cash value; display only, never a limit. Rewarded activity, never blended with ordinary.
   */
  offerCoinsEarned: Metric,
});
export type OverviewMetrics = z.infer<typeof OverviewMetrics>;

export const StatsOverviewResponse = z.object({
  definitionsVersion: z.number().int(),
  timezone: z.literal("UTC"),
  generatedAt: IsoUtc,
  lastAggregatedAt: IsoUtc.nullable(),
  online: z.object({
    count: z.number().int().min(0),
    asOf: IsoUtc,
    windowSeconds: z.number().int(),
    method: z.string(),
  }),
  ranges: z.record(StatsRange, z.object({ from: IsoUtc, to: IsoUtc, metrics: OverviewMetrics })),
  /** Human-readable method notes ("How we count"). */
  notes: z.array(z.string()),
  /** True when development fixtures could be present in the data (never true in production). */
  includesTestData: z.boolean(),
});
export type StatsOverviewResponse = z.infer<typeof StatsOverviewResponse>;

/** How many UTC days GET /stats/daily returns (the same window as the "last30d" range; raw activity is kept 30 days). */
export const STATS_DAILY_DAYS = 30;

/** One UTC day of town-wide metrics (same filters and definitions as the overview). */
export const StatsDailyPoint = z.object({
  /** UTC day, YYYY-MM-DD. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "utcDay"),
  /** True for the current UTC day: it is still being counted. */
  partial: z.boolean(),
  /** `uniqueVisitors` is the distinct count for that one day (never summed into a rolling figure). */
  metrics: OverviewMetrics,
});
export type StatsDailyPoint = z.infer<typeof StatsDailyPoint>;

/**
 * GET /stats/daily: the town's daily series for the last STATS_DAILY_DAYS UTC days (oldest first, today last), computed
 * from retained raw activity with the overview's filters. Read-only; a day with no activity is a real zero.
 */
export const StatsDailyResponse = z.object({
  definitionsVersion: z.number().int(),
  timezone: z.literal("UTC"),
  generatedAt: IsoUtc,
  days: z.array(StatsDailyPoint).max(STATS_DAILY_DAYS),
  /** Human-readable method note for the trend lines. */
  method: z.string(),
  includesTestData: z.boolean(),
});
export type StatsDailyResponse = z.infer<typeof StatsDailyResponse>;

export const RecentArrival = z.object({ countryCode: z.string().nullable(), at: IsoUtc });
export const PresenceResponse = z.object({
  online: z.number().int().min(0),
  asOf: IsoUtc,
  windowSeconds: z.number().int(),
  /** Country-only, cohort-thresholded; may be empty. */
  recentArrivals: z.array(RecentArrival).max(10),
});
export type PresenceResponse = z.infer<typeof PresenceResponse>;

export const AnalyticsEventRecord = z.object({
  id: Uuid,
  type: AnalyticsEventType,
  at: IsoUtc,
  propertyId: PropertyId.nullable(),
});
